import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';

import { AnswerRow } from './AnswerRow';
import {
  AnswerInput,
  DefectTypeOption,
  SubQuestionAnswerInput,
  SubQuestionInput,
} from './ConditionFormEditor';
import { SubQuestionEditor } from './SubQuestionEditor';

export const AnswerEditor = ({
  answer,
  answerIndex,
  questionBrandIds,
  onUpdate,
  onDelete,
  onAddSubQuestion,
  onDeleteSubQuestion,
  onUpdateSubQuestion,
  onAddSubQuestionAnswer,
  onUpdateSubQuestionAnswer,
  onDeleteSubQuestionAnswer,
  defectTypeOptions,
  showErrors,
}: {
  answer: AnswerInput;
  answerIndex: number;
  onUpdate: (updates: Partial<AnswerInput>) => void;
  onDelete: () => void;
  onAddSubQuestion: () => void;
  onDeleteSubQuestion: () => void;
  onUpdateSubQuestion: (updates: Partial<SubQuestionInput>) => void;
  onAddSubQuestionAnswer: () => void;
  onUpdateSubQuestionAnswer: (
    subAnswerIndex: number,
    updates: Partial<SubQuestionAnswerInput>
  ) => void;
  onDeleteSubQuestionAnswer: (subAnswerIndex: number) => void;
  defectTypeOptions: DefectTypeOption[];
  showErrors: boolean;
  questionBrandIds?: string[];
}) => {
  const { t, i18n } = useLingui();

  const answerTextLabel = t({
    id: 'settings.condition-forms.answer-text',
    message: 'Answer text',
  });
  const defectsLabel = t({
    id: 'settings.condition-forms.linked-defects',
    message: 'Linked defect types (optional)',
  });
  const defectsPlaceholder = t({
    id: 'settings.condition-forms.select-defects',
    message: 'Select defect types',
  });

  return (
    <Box>
      <Stack gap="0.75rem">
        <AnswerRow
          header={
            <Title size="400" weight="bold">
              {i18n._({
                id: 'settings.condition-forms.answer',
                message: 'Answer {order}',
                values: { order: answerIndex + 1 },
              })}
            </Title>
          }
          value={{ text: answer.text, defects: answer.defects }}
          onUpdate={onUpdate}
          onDelete={onDelete}
          deleteAriaLabel={t({
            id: 'settings.condition-forms.delete-answer',
            message: 'Delete answer',
          })}
          textLabel={answerTextLabel}
          textPlaceholder={answerTextLabel}
          defectsLabel={defectsLabel}
          defectsPlaceholder={defectsPlaceholder}
          defectTypeOptions={defectTypeOptions}
          showErrors={showErrors}
        />

        {answer.subQuestion && (
          <SubQuestionEditor
            subQuestion={answer.subQuestion}
            onUpdate={onUpdateSubQuestion}
            onDelete={onDeleteSubQuestion}
            onAddAnswer={onAddSubQuestionAnswer}
            onUpdateAnswer={onUpdateSubQuestionAnswer}
            onDeleteAnswer={onDeleteSubQuestionAnswer}
            defectTypeOptions={defectTypeOptions}
            showErrors={showErrors}
            questionBrandIds={questionBrandIds}
          />
        )}

        {!answer.subQuestion && (
          <Button variant="secondary" size="small" onPress={onAddSubQuestion}>
            <IconAdd />
            <Trans id="settings.condition-forms.add-sub-question">Add sub-question</Trans>
          </Button>
        )}
      </Stack>
    </Box>
  );
};
